/**
 * Content Hub (DAM) asset host: brand logos, illustrations, airline and payment icons,
 * tour-operator badges, favicons, certificates and campaign PDFs. ImageKit (MPH-618) — it
 * takes Cloudinary's transformation syntax, but no cloud-name segment, no URL signatures, and
 * paths are case-sensitive where Cloudinary's were not.
 */

import type { Localization } from '@/app/components/search/common/Localization.ts';

export const CONTENT_HUB_BASE_URL = 'https://media.staticroot.com';

/** Delivery path that transformations are hung off, for the assets that use one. */
export const CONTENT_HUB_UPLOAD_URL = `${CONTENT_HUB_BASE_URL}/image/upload`;

/** Illustrations published per brand under `DAM/SVG/Illustrations/<brand>/`. */
export enum BRAND_IMAGES {
  DEFERRED_PAYMENTS = 'deferred_payments.svg',
  DEFERRED_PAYMENTS_MESSAGE = 'deferred_payments_message.svg',
  MAP_CARD = 'map_card.svg',
  NO_RESULTS = 'no_results.svg',
  OVER_FILTERED = 'over_filtered.svg',
  TIME_TRAVEL = 'time_travel.svg',
  OVERNIGHT = 'overnight.svg',
  VOUCHER = 'voucher.svg',
  DEPOSIT = 'deposit.svg',
}

const BLACKLISTED_IATA_CODES = ['DEFAULT', 'XXX'];

/**
 * Most airline logos are published as SVG, so the `w_24` below is decorative for them and every
 * render site has to set its own width and height. The code is uppercased because the icons are,
 * and this host — unlike Cloudinary — will not resolve another casing.
 */
export const getAirlineLogoUrl = (airlineCode?: string): string | null => {
  if (!airlineCode) return null;
  const code = airlineCode.toUpperCase();
  if (BLACKLISTED_IATA_CODES.includes(code)) return null;
  return `${CONTENT_HUB_UPLOAD_URL}/c_fill,w_24,q_auto,f_auto/v1/DAM/Commercial%20Partners/Airlines/Icons/${code}`;
};

const getBrandImageBasePath = ({ brand, themeName }: Localization) =>
  `${CONTENT_HUB_UPLOAD_URL}/DAM/SVG/Illustrations/${themeName === 'pink-panther' ? 'lastminute' : brand}/`;

export const getBrandImageUrl = (image: BRAND_IMAGES, localization: Localization) =>
  `${getBrandImageBasePath(localization)}${image}`;

const TOUR_OPERATORS_PATH = `${CONTENT_HUB_BASE_URL}/DAM/Commercial%20Partners/Tour%20Operators`;

/**
 * Tour-operator badge artwork, as the urls to try in order. The DAM keeps two folders and stores
 * each operator in exactly one of them, so the folder — not the market — is what decides where a
 * logo lives: results in either market can carry a tour operator filed under the other folder, and
 * trying only the market's own folder is what left those cards without a badge (MPH-1081). Each
 * market therefore leads with its own folder and falls back to the other one.
 *
 * This host is case-sensitive and the DAM is not consistent about casing either: `TO FR logos` is
 * upper-cased throughout, `TO logos` is mostly lower-cased with a few upper-cased files, so the
 * latter is worth trying both ways (MPH-618). Spaces are underscores in both folders, and these
 * assets carry no delivery path, version or extension.
 */
export const getTourOperatorLogoUrls = (isFR: boolean, code: string | undefined): string[] => {
  if (!code) return [];
  const file = code.replaceAll(' ', '_');
  const fr = `TO%20FR%20logos/${file.toUpperCase()}`;
  const de = `TO%20logos/${file.toLowerCase()}`;
  const deUpperCased = `TO%20logos/${file.toUpperCase()}`;
  const paths = isFR ? [fr, de, deUpperCased] : [de, deUpperCased, fr];
  return paths.map((path) => `${TOUR_OPERATORS_PATH}/${path}`);
};
